import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import { View, StyleSheet, Platform, useWindowDimensions, TextInput } from 'react-native';
import { Pressable } from 'react-native-web';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesome, FontAwesome6, MaterialCommunityIcons, MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import LottieView from '@utils/lottieAnimation';
import { Audio } from 'expo-av';
import { CHANGE_THEME, STATUSBAR_HEIGHT, SHOW_FEEDBACK_MODAL, SHOW_NOTIFICATIONS_MODAL, HIDE_NOTIFICATIONS_MODAL, TOGGLE_NOTIFICATIONS_PANEL, NEW_NOTIFICATION, CLEAR_NOTIFICATIONS, HIDE_USER_CONTEXT_MODAL, TOGGLE_CONTEXT, CONTEXT_SIZE_TOGGLE, LOGGED_OUT, SHOW_HELP_MODAL } from '@reduxLocal/actions/types';
import SocketManager from '@utils/socket';
import LanguagePopover from '@navigation/HeaderBar/components/Langauge-popover';
import { DefaultRootState, persistor, store } from '@reduxLocal/persistState';
import useGlobalUser from '@hooks/getGlobalUser';
import NotificationItem from '@components/modals/NotificationModal/components/NotificationItem';
import { Badge, IconButton, Tooltip, Menu, MenuItem, MenuList } from "@mui/material";
const socketManager = new SocketManager('data');
// NOTE: notificationsSocketManager removed - notification handling centralized in DashStack.tsx
import { useColorScheme } from '../../../App';
import GetAvatarImage from '@hooks/getAvatarImage';
import { Button, H5, Popover, XStack, Separator, SizableText, YStack, Paragraph } from 'tamagui';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { useNavigation } from '@react-navigation/native';
import apis from '@api/api';
import SvgQRCode from 'react-native-qrcode-svg';
import { HOST } from '@api/constant';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import modernStyles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { Title } from '@constants/Themed';
import { useTranslation } from 'react-i18next';
// Hero Button Styles
const HERO_PRIMARY_COLOR = '#5CC0EB'; // Sky Blue from Hero.tsx
const HERO_TEXT_COLOR = '#FFFFFF';

const getHeroButtonStyle = (theme: boolean, variant: 'solid' | 'outline' = 'solid') => {
  if (variant === 'solid') {
    return {
      backgroundColor: HERO_PRIMARY_COLOR,
      shadowColor: HERO_PRIMARY_COLOR,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 6,
      borderWidth: 0,
    };
  } else {
    return {
      backgroundColor: theme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 41, 59, 0.5)',
      borderWidth: 2,
      borderColor: 'rgba(92, 192, 235, 0.3)',
    };
  }
};

// Hover Label component for status bar items
const HoverLabel = ({ label, children, position = 'bottom' }: { label: string; children: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionStyles = {
    top: { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 12 },
    bottom: { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 12 },
    left: { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: 12 },
    right: { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: 12 },
  };

  if (Platform.OS !== 'web') {
    return <>{children}</>;
  }



  return (
    <View style={{ position: 'relative' }}>
      <Pressable
        onHoverIn={() => setIsVisible(true)}
        onHoverOut={() => setIsVisible(false)}
        style={{ position: 'relative' }}
      >
        {children}
        {isVisible && (
          <View style={[{
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            paddingHorizontal: 10,
            paddingVertical: 6,
            borderRadius: 6,
            zIndex: 1000,
            minWidth: 'max-content',
            whiteSpace: 'nowrap',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 5,
            ...positionStyles[position]
          } as any]}>
            <Title style={{ color:"white", fontSize: 13, fontWeight: '600' }}>
              {label}
            </Title>
            {/* Triangle arrow */}
            <View style={[{
              position: 'absolute',
              width: 0,
              height: 0,
              borderLeftWidth: 6,
              borderRightWidth: 6,
              borderLeftColor: 'transparent',
              borderRightColor: 'transparent',
              left: '50%',
              transform: 'translateX(-50%)',
              ...(position === 'bottom' ? {
                top: -6,
                borderBottomWidth: 6,
                borderBottomColor: 'rgba(0, 0, 0, 0.85)'
              } : {
                bottom: -6,
                borderTopWidth: 6,
                borderTopColor: 'rgba(0, 0, 0, 0.85)'
              })
            } as any]} />
          </View>
        )}
      </Pressable>
    </View>
  );
};

const BORDERS = 20;

const StatusBar = ({
  availableWidth = 400,
  isSmallScreen = false,
  searchVal = "",
  setSearchVal,
  searchExpanded = false,
  setSearchExpanded,
  onSearchSubmit
}: { 
  availableWidth?: number; 
  isSmallScreen?: boolean;
    searchVal?: string;
    setSearchVal?: (val: string) => void;
    searchExpanded?: boolean;
    setSearchExpanded?: (expanded: boolean) => void;
    onSearchSubmit?: (query: string) => void;
}) => {
  const authenticated = useSelector((state: DefaultRootState) => state.promiseData.isAuthenticated);
  const notificationCount = useSelector((state: DefaultRootState) => state.notifications.count);
  const notifications = useSelector((state: DefaultRootState) => state.notifications);
  const notificationVisible = useSelector((state: DefaultRootState) => state.modals.notificationsPanel.modalDisplayed);
  const minimized = useSelector((state: DefaultRootState) => state.UI.contextMinimized);
  const notificationRef = useRef<any>(null);
  const userId = useGlobalUser();
  const toolbarVisible = useSelector((state: DefaultRootState) => state.modals.toolbar.toolbarDisplayed);
  const { t } = useTranslation();

  // Memoize user data to prevent unnecessary re-renders when user hasn't changed
  const user = selectUser();
  const userData = useMemo(() => ({
    id: user?.id,
    userId: userId,
    name: user?.name,
    surname: user?.surname,
    email: user?.email,
    authenticated
  }), [user?.id, userId, user?.name, user?.surname, user?.email, authenticated]);

  // Memoize QR code value to prevent recalculation on every render
  const qrCodeValue = useMemo(() =>
    `https://api.smartsaas.pro/profiles/${userData.name}%20${userData.surname}?id=${userData.id}`,
    [userData.name, userData.surname, userData.id]
  );

  // Memoize profile URL to prevent recalculation
  const profileUrl = useMemo(() =>
    `${HOST}/profiles/${userData.name}%20${userData.surname}?id=${userData.id}`,
    [userData.name, userData.surname, userData.id]
  );

  const socket = socketManager.getSocket(userId);
  // NOTE: notificationsSocket removed - notification handling centralized in DashStack.tsx
  const [tempList, setTempList] = useState([]);
  const dispatch = useDispatch();
  const [breakpoint, setBreakPoint] = useState(0);
  const theme = themeSelector();
  const [event, setEvent] = useState([]);
  const { colorScheme, setColorScheme } = useColorScheme();
  const navigation = useNavigation();
  const popRef = useRef<Popover>(null);
  const [burgerMenuAnchor, setBurgerMenuAnchor] = useState<null | HTMLElement>(null);
  const [burgerMenuAnchor2, setBurgerMenuAnchor2] = useState<null | HTMLElement>(null);
  const [isQrCodeHovered, setIsQrCodeHovered] = useState(false);
  const [notificationItemVisible, setNotificationItemVisible] = useState(false);
  const [notificationItemOpacity, setNotificationItemOpacity] = useState(0);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fadeAnimationRef = useRef<number | null>(null);

  // Calculate how many icons can fit in available space
  const totalStatusBarItems = 8; // Total number of status bar items including search (excluding user profile which is always visible)
  const searchExtraWidth = searchExpanded ? 160 : 0; // Extra width needed when search is expanded (200 total - 40 collapsed = 160)
  const requiredWidth = (totalStatusBarItems * 40 * 3) + searchExtraWidth; // Your specified calculation plus search expansion
  const shouldUseBurgerMenu = availableWidth < requiredWidth;
  const minVisibleIcons = 2; // Always show notifications + user profile at minimum
  
  // When using burger menu, show only high-priority items + burger button
  const visibleIconCount = shouldUseBurgerMenu ? minVisibleIcons : totalStatusBarItems;

  // Define all status bar items
  const statusBarItems = useMemo(() => [
    {
      id: 'search',
      label: searchExpanded ? 'Close Search' : 'Search',
      priority: 0, // Highest priority - always visible first
      icon: <FontAwesome5 color={theme ? "grey" : "white"} name={searchExpanded && searchVal ? "times" : "search"} size={16} />,
      render: () => {
        if (searchExpanded) {
          return (
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              width: 200,
              height: 40,
              position: 'relative',
              marginRight: 8
            }}>
              <TextInput
                value={searchVal}
                onChangeText={setSearchVal}
                placeholder="Search..."
                placeholderTextColor={getModernTextColor(theme, 'muted')}
                style={[
                  {
                    flex: 1,
                    height: 40,
                    paddingHorizontal: 15,
                    paddingRight: 45,
                    borderRadius: 20,
                    borderWidth: 1,
                    fontSize: 14,
                    color: getModernTextColor(theme),
                  },
                  getModernCardStyle(theme, 'small'),
                  {
                    ...(Platform.OS === 'web' && {
                      backdropFilter: 'blur(16px)',
                      boxShadow: theme
                        ? 'inset 0 2px 8px rgba(0,0,0,0.06)'
                        : 'inset 0 2px 8px rgba(255,255,255,0.03)',
                    }),
                  }
                ]}
                autoFocus={searchExpanded}
                onSubmitEditing={() => {
                  if (searchVal && searchVal.trim() && onSearchSubmit) {
                    onSearchSubmit(searchVal.trim());
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    if (!searchVal && setSearchExpanded) setSearchExpanded(false);
                  }, 150);
                }}
              />
              <Pressable
                onPress={() => {
                  if (searchExpanded && searchVal && searchVal.trim()) {
                    // Submit search if there's text
                    if (onSearchSubmit) {
                      onSearchSubmit(searchVal.trim());
                    }
                  } else if (setSearchExpanded) {
                    setSearchExpanded(!searchExpanded);
                  }
                }}
                style={[
                  modernStyles.modernActionButton,
                  getModernCardStyle(theme, 'small'),
                  {
                    position: 'absolute',
                    right: 0,
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    zIndex: 1,
                    ...(Platform.OS === 'web' && {
                      backdropFilter: 'blur(16px)',
                      boxShadow: theme
                        ? '0 2px 12px rgba(0,0,0,0.08)'
                        : '0 2px 12px rgba(255,255,255,0.05)',
                      transition: 'all 0.2s ease',
                    }),
                  }
                ]}
              >
                <FontAwesome5
                  name={searchExpanded && searchVal ? "times" : "search"}
                  size={16}
                  color={getModernTextColor(theme)}
                />
              </Pressable>
            </View>
          );
        } else {
          return (
            <HoverLabel label="Search">
              <Pressable
                onPress={() => setSearchExpanded && setSearchExpanded(!searchExpanded)}
                style={[
                  modernStyles.modernActionButton,
                  getModernCardStyle(theme, 'small'),
                  {
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    ...(Platform.OS === 'web' && {
                      backdropFilter: 'blur(16px)',
                      boxShadow: theme
                        ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                        : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                      transition: 'all 0.2s ease',
                    }),
                  }
                ]}
              >
                <FontAwesome5
                  color={getModernTextColor(theme)}
                  name="search"
                  size={16}
                />
              </Pressable>
            </HoverLabel>
          );
        }
      },
      burgerAction: () => setSearchExpanded && setSearchExpanded(!searchExpanded)
    },
    {
      id: 'language',
      label: 'Language Settings',
      priority: 7,
      icon: <MaterialIcons name="language" size={16} />,
      render: () => (
        <HoverLabel label="Language Settings">
          <View style={[
            modernStyles.modernActionButton,
            getModernCardStyle(theme, 'small'),
            {
              width: 40,
              height: 40,
              borderRadius: 12,
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(16px)',
                boxShadow: theme
                  ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                  : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                transition: 'all 0.2s ease',
              }),
            }
          ]}>
            <LanguagePopover />
          </View>
        </HoverLabel>
      ),
      burgerAction: null // Special case - language popover needs custom handling
    },
    {
      id: 'notifications',
      label: 'View Notifications',
      priority: 1, // High priority - always visible
      icon: <MaterialIcons name="notifications" size={16} />,
      render: () => (
        <HoverLabel label="View Notifications">
          <View style={{ position: 'relative' }}>
            <Pressable
              onPress={() => {
                if (!notificationVisible) {
                  dispatch({ type: CLEAR_NOTIFICATIONS })
                }
                dispatch({ type: TOGGLE_NOTIFICATIONS_PANEL });
                if (!minimized) dispatch({ type: CONTEXT_SIZE_TOGGLE })
              }}
              style={[
                modernStyles.modernActionButton,
                getModernCardStyle(theme, 'small'),
                {
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  ...(Platform.OS === 'web' && {
                    backdropFilter: 'blur(16px)',
                    boxShadow: theme
                      ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                      : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                    transition: 'all 0.2s ease',
                  }),
                }
              ]}
            >
              <MaterialCommunityIcons name="bell" size={18} color={theme ? "black" : "white"} />
            </Pressable>
            {notificationCount > 0 && (
              <View style={[
                modernStyles.modernActionButton,
                {
                  position: 'absolute',
                  top: 4,
                  right: -4,
                  minWidth: 20,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor: '#ef4444',
                  borderWidth: 2,
                  borderColor: theme ? 'white' : '#1e293b',
                  paddingHorizontal: notificationCount > 9 ? 6 : 4,
                  justifyContent: 'center',
                  alignItems: 'center',
                  ...(Platform.OS === 'web' && {
                    boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                  }),
                }
              ]}>
                <Title style={{
                  color: 'white',
                  fontSize: 10,
                  fontWeight: '700',
                  textAlign: 'center',
                  lineHeight: 12,
                }}>
                  {notificationCount > 99 ? '99+' : notificationCount}
                </Title>
              </View>
            )}
          </View>
        </HoverLabel>
      ),
      burgerAction: () => {
        if (!notificationVisible) {
          dispatch({ type: CLEAR_NOTIFICATIONS })
        }
        dispatch({ type: TOGGLE_NOTIFICATIONS_PANEL });
        if (!minimized) dispatch({ type: CONTEXT_SIZE_TOGGLE })
      }
    },
    {
      id: 'feedback',
      label: 'Send Feedback',
      priority: 5,
      icon: <MaterialCommunityIcons color={theme ? "grey" : "white"} name="emoticon-sad-outline" size={16} />,
      render: () => (
        <HoverLabel label="Send Feedback">
          <Pressable
            onPress={() => dispatch({ type: SHOW_FEEDBACK_MODAL })}
            style={[
              modernStyles.modernActionButton,
              getModernCardStyle(theme, 'small'),
              {
                width: 40,
                height: 40,
                borderRadius: 12,
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(16px)',
                  boxShadow: theme
                    ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                    : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                  transition: 'all 0.2s ease',
                }),
              }
            ]}
          >
            <LottieView
              source={require('@assets/animations/emojis/sad-emoji.json')}
              resizeMode='contain'
              style={styles.feedbackIcon}
              loop={false}
            />
          </Pressable>
        </HoverLabel>
      ),
      burgerAction: () => dispatch({ type: SHOW_FEEDBACK_MODAL })
    },
    {
      id: 'theme',
      label: `Switch to ${theme ? 'Dark' : 'Light'} Theme`,
      priority: 4,
      icon: <MaterialCommunityIcons color={theme ? "grey" : "white"} name="theme-light-dark" size={16} />,
      render: () => (
        <HoverLabel label={`Switch to ${theme ? 'Dark' : 'Light'} Theme`}>
          <Pressable
            onPress={() => {
              setColorScheme(colorScheme === 'light' ? 'dark' : 'light');
              dispatch({ type: CHANGE_THEME, payload: { lightTheme: !theme, themeColour: theme ? 'light' : 'dark' } })
            }}
            style={[
              modernStyles.modernActionButton,
              getModernCardStyle(theme, 'small'),
              {
                width: 40,
                height: 40,
                borderRadius: 12,
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(16px)',
                  boxShadow: theme
                    ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                    : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                  transition: 'all 0.2s ease',
                }),
              }
            ]}
          >
            <MaterialCommunityIcons
              color={getModernTextColor(theme)}
              name="theme-light-dark"
              size={20}
            />
          </Pressable>
        </HoverLabel>
      ),
      burgerAction: () => {
        setColorScheme(colorScheme === 'light' ? 'dark' : 'light');
        dispatch({ type: CHANGE_THEME, payload: { lightTheme: !theme, themeColour: theme ? 'light' : 'dark' } });
      }
    },
    {
      id: 'community',
      label: 'Join our Community',
      priority: 3,
      icon: <MaterialCommunityIcons color={theme ? "grey" : "white"} name="account-group" size={16} />,
      render: () => (
        <HoverLabel label="Join our Community">
          <Pressable
            onPress={() => {
              (navigation as any).navigate('Community');
            }}
            style={[
              modernStyles.modernActionButton,
              getModernCardStyle(theme, 'small'),
              {
                width: 40,
                height: 40,
                borderRadius: 12,
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(16px)',
                  boxShadow: theme
                    ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                    : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                  transition: 'all 0.2s ease',
                }),
              }
            ]}
          >
            <MaterialCommunityIcons
              color={getModernTextColor(theme)}
              name="account-group"
              size={20}
            />
          </Pressable>
        </HoverLabel>
      ),
      burgerAction: () => (navigation as any).navigate('Community')
    },
    {
      id: 'documentation',
      label: 'View Documentation',
      priority: 8,
      icon: <MaterialIcons color={
        theme ? "grey" : "white"
      } name="help-outline" size={16} />,
      render: () => (
        <HoverLabel label="View Documentation">
          <Pressable
            onPress={() => {
              if (Platform.OS === 'web') {
                window.open('https://smartsaas.net/docs', '_blank');
              }
            }}
            style={[
              modernStyles.modernActionButton,
              getModernCardStyle(theme, 'small'),
              {
                width: 40,
                height: 40,
                borderRadius: 12,
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(16px)',
                  boxShadow: theme
                    ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                    : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                  transition: 'all 0.2s ease',
                }),
              }
            ]}
          >
            <MaterialIcons
              color={getModernTextColor(theme)}
              name="help-outline"
              size={20}
            />
          </Pressable>
        </HoverLabel>
      ),
      burgerAction: () => {
        if (Platform.OS === 'web') {
          window.open('https://smartsaas.co.uk/docs', '_blank');
        }
      }
    },
    {
      id: 'support',
      label: 'Get Support',
      priority: 8,
      icon: <MaterialCommunityIcons color={theme ? "grey" : "white"} name="lifebuoy" size={16} />,
      render: () => (
        <HoverLabel label="Get Support">
          <Pressable
            onPress={() => {
              dispatch({type:SHOW_HELP_MODAL})
            }}
            style={[
              modernStyles.modernActionButton,
              getModernCardStyle(theme, 'small'),
              {
                width: 40,
                height: 40,
                borderRadius: 12,
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(16px)',
                  boxShadow: theme
                    ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                    : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                  transition: 'all 0.2s ease',
                }),
              }
            ]}
          >
            <MaterialCommunityIcons
              color={getModernTextColor(theme)}
              name="lifebuoy"
              size={20}
            />
          </Pressable>
        </HoverLabel>
      ),
      burgerAction: () => dispatch({type: SHOW_HELP_MODAL})
    }
  ], [theme, colorScheme, notificationCount, notificationVisible, minimized, navigation, dispatch, searchExpanded, searchVal, setSearchVal, setSearchExpanded]);

  // Sort items by priority and determine which to show
  const sortedItems = statusBarItems.sort((a, b) => a.priority - b.priority);
  const visibleItems = shouldUseBurgerMenu 
    ? sortedItems.slice(0, minVisibleIcons - 1) // Reserve space for burger menu (notifications only when compact)
    : sortedItems; // Show all items when space is available
  const hiddenItems = shouldUseBurgerMenu 
    ? sortedItems.slice(minVisibleIcons - 1) // All other items go to burger menu
    : [];

  useEffect(() => {
    notificationRef.current?.play(0, 25);

    // Only connect sockets if user is authenticated
    if (authenticated && userId) {
      socket.connect();
      socket.emit('connection');

      // NOTE: Notification socket connection removed - handled centrally in DashStack.tsx
    }

    return () => {
      // DON'T disconnect sockets here - let SocketManager handle lifecycle
      // Disconnecting on component unmount causes issues when modals appear
      // Sockets should stay connected as long as user is authenticated
    };
  }, [authenticated, userId]);


  const _handleLogout = useCallback(async () => {
    (navigation as any).navigate('Loading', { params: { flagHold: true } });

    await apis.logoutUser()
      .then(async () => {
        dispatch({ type: LOGGED_OUT }) //Clears redux store
        await persistor.purge().then(() => {
          (navigation as any).navigate('RootStack', {
            screen: 'Login',
          });
        });
      });
  }, [navigation, dispatch])



  const playSound = async () => {
    const { sound } = await Audio.Sound.createAsync(require('@assets/sound/smartsaasping.wav'));
    // await sound.playAsync();
  };

  // NOTE: Socket 'event' listener removed - notifications are handled centrally in DashStack.tsx
  // StatusBar subscribes to Redux NEW_NOTIFICATION actions to avoid duplicate badge increments
  // This ensures each notification only increments the badge once

  useEffect(() => {
    // Subscribe to Redux actions to receive notifications from DashStack
    // This prevents duplicate NEW_NOTIFICATION dispatches that cause badge to increment multiple times
    const unsubscribe = store.subscribe(() => {
      const lastAction = store.getState().lastAction;
      if (lastAction && lastAction.type === NEW_NOTIFICATION) {
        const newEvent = lastAction.payload;
        // Add to tempList for notification item animations (without dispatching NEW_NOTIFICATION again)
        setTempList(list => [...list, newEvent]);
        playSound();
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // NOTE: Notification socket listeners are now handled centrally in DashStack.tsx
  // StatusBar receives notifications through Redux (NEW_NOTIFICATION action)
  // This prevents duplicate socket connections and ensures single source of truth

  useEffect(() => {
    if (tempList.length) {
      dispatch({ type: SHOW_NOTIFICATIONS_MODAL });

      let isMounted = true;
      const timeoutIds: NodeJS.Timeout[] = [];

      const processNotifications = async () => {
        for (let i = 0; i < tempList.length; i++) {
          if (!isMounted) break; // Exit if unmounted

          const item = tempList[i];
          setEvent(prevList => [...prevList, item]);
          setTempList(prevTempList => prevTempList.filter(tempItem => tempItem !== item));

          if (i < tempList.length - 1) {
            await new Promise(resolve => {
              const promiseTimeoutId = setTimeout(resolve, 5000);
              timeoutIds.push(promiseTimeoutId);
            });
          }
        }
      };

      processNotifications().then(() => {
        if (isMounted) {
          dispatch({ type: HIDE_NOTIFICATIONS_MODAL });
        }
      });

      return () => {
        isMounted = false;
        timeoutIds.forEach(id => clearTimeout(id));
      };
    }
  }, [tempList, dispatch]);

  // Show notification item when new event arrives (from either event state or Redux notifications)
  useEffect(() => {
    // Check if we have notifications from either source
    const hasEventNotification = event.length > 0 && event[event.length - 1];
    const hasReduxNotification = notificationCount > 0 && notifications.lastNotification;

    if (hasEventNotification || hasReduxNotification) {
      setNotificationItemVisible(true);
      setNotificationItemOpacity(1);
      // Clear any existing hover timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      // Clear any existing fade animation
      if (fadeAnimationRef.current) {
        cancelAnimationFrame(fadeAnimationRef.current);
        fadeAnimationRef.current = null;
      }

      // Start auto-hide timer: fade out after 5 seconds
      hoverTimeoutRef.current = setTimeout(() => {
        // Fade out animation using requestAnimationFrame for smoother animation
        const fadeOutDuration = 500; // 500ms fade out
        const startTime = Date.now();

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / fadeOutDuration, 1);
          const newOpacity = 1 - progress;

          setNotificationItemOpacity(newOpacity);

          if (progress < 1) {
            fadeAnimationRef.current = requestAnimationFrame(animate);
          } else {
            setNotificationItemVisible(false);
            setNotificationItemOpacity(0);
            fadeAnimationRef.current = null;
          }
        };

        fadeAnimationRef.current = requestAnimationFrame(animate);
      }, 5000); // Auto-hide after 5 seconds
    }
  }, [event.length, notificationCount, notifications.lastNotification]);

  const handleCloseModal = () => {
    tempList.forEach(item => {
      setEvent(prevList => [...prevList, item]);
      setTempList(prevTempList => prevTempList.filter(tempItem => tempItem !== item));
    });
  };

  // Handle notification item hover - interrupt auto-hide and keep visible
  const handleNotificationItemHover = useCallback(() => {
    // Clear any existing timeout and animation to interrupt auto-hide
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (fadeAnimationRef.current) {
      cancelAnimationFrame(fadeAnimationRef.current);
      fadeAnimationRef.current = null;
    }

    // Reset opacity to full when hovering (interrupt fade out)
    setNotificationItemOpacity(1);
    setNotificationItemVisible(true);
  }, []);

  // Handle notification item hover out - restart auto-hide timer
  const handleNotificationItemHoverOut = useCallback(() => {
    // Clear any existing timeout and animation
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (fadeAnimationRef.current) {
      cancelAnimationFrame(fadeAnimationRef.current);
      fadeAnimationRef.current = null;
    }

    // Restart auto-hide timer: fade out after 5 seconds
    hoverTimeoutRef.current = setTimeout(() => {
      // Fade out animation using requestAnimationFrame for smoother animation
      const fadeOutDuration = 500; // 500ms fade out
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / fadeOutDuration, 1);
        const newOpacity = 1 - progress;

        setNotificationItemOpacity(newOpacity);

        if (progress < 1) {
          fadeAnimationRef.current = requestAnimationFrame(animate);
        } else {
          setNotificationItemVisible(false);
          setNotificationItemOpacity(0);
          fadeAnimationRef.current = null;
        }
      };

      fadeAnimationRef.current = requestAnimationFrame(animate);
    }, 5000); // Auto-hide after 5 seconds from hover out
  }, []);

  // Handle notification item press - open modal and hide item
  const handleNotificationItemPress = useCallback(() => {
    // Clear hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Clear fade animation
    if (fadeAnimationRef.current) {
      cancelAnimationFrame(fadeAnimationRef.current);
      fadeAnimationRef.current = null;
    }

    // Open notification modal
    if (!notificationVisible) {
      dispatch({ type: CLEAR_NOTIFICATIONS });
    }
    dispatch({ type: TOGGLE_NOTIFICATIONS_PANEL });
    if (!minimized) {
      dispatch({ type: CONTEXT_SIZE_TOGGLE });
    }

    // Hide the notification item
    setNotificationItemVisible(false);
    setNotificationItemOpacity(0);
  }, [notificationVisible, minimized, dispatch]);

  // Cleanup timeout and animation on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (fadeAnimationRef.current) {
        cancelAnimationFrame(fadeAnimationRef.current);
      }
    };
  }, []);


  return (
    <View 
      style={[styles.statusBarContainer, { paddingHorizontal: 20, paddingRight: toolbarVisible ? 50 : undefined }]}>
      {notificationItemVisible && (() => {
        // Get the latest notification from either event state or Redux
        const latestEventNotification = event.length > 0 ? event[event.length - 1] : null;
        const latestReduxNotification = notifications.lastNotification;
        const notificationToShow = latestEventNotification || latestReduxNotification;

        return notificationToShow ? (
          <Pressable
            onPress={handleNotificationItemPress}
            onHoverIn={handleNotificationItemHover}
            onHoverOut={handleNotificationItemHoverOut}
            style={{
              alignContent: 'flex-end',
              alignItems: 'flex-end',
              alignSelf: 'flex-end',
              position: 'absolute',
              minWidth: 300,
              top: 75,
              right: "10%",
              opacity: notificationItemOpacity,
              ...(Platform.OS === 'web' && {
                transition: 'opacity 0.3s ease-out',
              }),
            }}
          >
            <NotificationItem item={notificationToShow} />
          </Pressable>
        ) : null;
      })()}
      <View style={styles.innerContainer} onLayout={e => setBreakPoint(e.nativeEvent.layout.width)}>
        <View style={styles.buttonContainer}>
          {/* Render visible items */}
          {visibleItems.map(item => (
            <View key={item.id}>
              {item.render()}
            </View>
          ))}

          {/* Burger menu for hidden items */}
          {shouldUseBurgerMenu && hiddenItems.length > 0 && (
            <HoverLabel label="More Options">
              <Pressable
                onPress={(event) => setBurgerMenuAnchor(event.currentTarget)}
                style={[
                  modernStyles.modernActionButton,
                  getModernCardStyle(theme, 'small'),
                  {
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    ...(Platform.OS === 'web' && {
                      backdropFilter: 'blur(16px)',
                      boxShadow: theme
                        ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                        : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                      transition: 'all 0.2s ease',
                    }),
                  }
                ]}
              >
                <MaterialIcons
                  color={getModernTextColor(theme)}
                  name="more-vert"
                  size={20}
                />
              </Pressable>
            </HoverLabel>
          )}

          {/* Material-UI Menu for burger menu items */}
          <Menu
            anchorEl={burgerMenuAnchor}
            open={Boolean(burgerMenuAnchor)}
            onClose={() => setBurgerMenuAnchor(null)}
            PaperProps={{
              style: {
                backgroundColor: theme ? "rgba(255,255,255,0.95)" : "rgba(30,41,59,0.95)",
                borderRadius: 12,
                minWidth: 200,
                padding: 8,
                backdropFilter: Platform.OS === 'web' ? 'blur(24px)' : undefined,
                boxShadow: Platform.OS === 'web'
                  ? (theme
                    ? '0 12px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)'
                    : '0 12px 48px rgba(255,255,255,0.08), 0 4px 16px rgba(255,255,255,0.04)'
                  ) : undefined,
              }
            }}
          >
            {hiddenItems
              .filter(item => item != null)
              .map(item => {
                // Special handling for language settings - render the actual popover component
                if (item.id === 'language') {
                  return (
                    <MenuItem
                      key={item.id}
                      sx={{
                        color: theme ? "#333333" : "white",
                        '&:hover': {
                          backgroundColor: theme ? "#f5f5f5" : "#444444",
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      {item.icon}
                      <View style={{ marginLeft: 8 }}>
                        <LanguagePopover />
                      </View>
                    </MenuItem>
                  );
                }

                // Regular menu items with icons
                return (
                  <MenuItem
                    key={item.id}
                    onClick={() => {
                      setBurgerMenuAnchor(null);
                      if (item.burgerAction) {
                        item.burgerAction();
                      }
                    }}
                    sx={{
                      color: theme ? "#333333" : "white",
                      '&:hover': {
                        backgroundColor: theme ? "#f5f5f5" : "#444444",
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {item.icon}
                    <span style={{ marginLeft: 8 }}>{item.label}</span>
                  </MenuItem>
                );
              })}
          </Menu>

          {/* User Profile - Always visible with highest priority */}
          <HoverLabel label="User Profile & Settings">
            <Pressable
            id="tutorial-profile-picture"
              onPress={(event) => setBurgerMenuAnchor2(event.currentTarget)}
              style={[
                modernStyles.modernActionButton,
                getModernCardStyle(theme, 'small'),
                {
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  padding: 4,
                  ...(Platform.OS === 'web' && {
                    backdropFilter: 'blur(16px)',
                    boxShadow: theme
                      ? '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                      : '0 4px 20px rgba(255,255,255,0.05), 0 2px 8px rgba(255,255,255,0.02)',
                    transition: 'all 0.2s ease',
                  }),
                }
              ]}
            >
              <GetAvatarImage style={[{
                width: 26,
                height: 26,
                borderRadius: 12,
                backgroundColor: theme ? 'white' : 'black'
              }]} />
            </Pressable>
          </HoverLabel>

          {/* User Profile Menu */}
          <Menu
            anchorEl={burgerMenuAnchor2}
            open={Boolean(burgerMenuAnchor2)}
            onClose={() => setBurgerMenuAnchor2(null)}
            PaperProps={{
              style: {
                backgroundColor: theme ? "rgba(255,255,255,0.95)" : "rgba(30,41,59,0.95)",
                borderRadius: 20,
                minWidth: 400,
                padding: 20,
                backdropFilter: Platform.OS === 'web' ? 'blur(24px)' : undefined,
                boxShadow: Platform.OS === 'web'
                  ? (theme
                    ? '0 12px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)'
                    : '0 12px 48px rgba(255,255,255,0.08), 0 4px 16px rgba(255,255,255,0.04)'
                  ) : undefined,
              }
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuList sx={{ padding: '10px', gap: '20px' }}>
              <View style={{ padding: 10, gap: 20 }}>
                <XStack flexWrap='wrap' style={[getModernCardStyle(theme, 'small'), { flexDirection: 'row', gap: 20, alignItems: 'center' }]}>
                <Pressable
                  style={{ flex: 1, minWidth: 100, position: 'relative' }}
                  onPress={async () => {
                    window.open(profileUrl);
                    setBurgerMenuAnchor2(null);
                  }}
                  onHoverIn={() => Platform.OS === 'web' && setIsQrCodeHovered(true)}
                  onHoverOut={() => Platform.OS === 'web' && setIsQrCodeHovered(false)}
                >
                  <View style={{ flex: 1, borderRadius: 20, padding: 10, gap: 10 }}>
                      <SvgQRCode size={100} value={qrCodeValue} />
                  </View>
                  {isQrCodeHovered && (
                    <View style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: 20,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <MaterialIcons name="launch" size={24} color="white" />
                    </View>
                  )}
                </Pressable>
                  <View style={[styles.userProfileContainer, { flex: 5 }]}>
                  <View style={styles.userNameContainer}>
                      <H5 numberOfLines={1} adjustsFontSizeToFit style={[styles.userText, { fontSize: undefined, fontWeight: '700', textAlign: "right", color: theme ? "#333333" : "white" }]}>{userData.name} {userData.surname}</H5>
                  </View>
                    <SizableText style={[styles.userText, { color: theme ? "#666666" : "#cccccc" }]}>{userData.email}</SizableText>
                </View>
              </XStack>

                <View style={[getModernCardStyle(theme, 'small'), { gap: 0 }]}>
                <Pressable
                  onPress={() => {
                    (navigation as any).navigate('Settings'); 
                    setBurgerMenuAnchor2(null);
                  }}
                  style={[
                    modernStyles.modernButton,
                    getHeroButtonStyle(theme, 'solid'),
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      backgroundColor: "transparent",
                      gap: 12,
                      shadowOpacity: 0,
                      marginBottom: 8,
                    }
                  ]}
                >
                    <FontAwesome name="user" color={theme ? "black" : "white"} size={16} />
                    <Title style={{ fontSize: 14, textAlign: "left", fontWeight: '600', color: theme ? "black" : "white" }}>Profile</Title>
                </Pressable>
                
                <Pressable
                  onPress={() => {
                    (navigation as any).navigate("Settings", { screen: "Upgrade" }); 
                    setBurgerMenuAnchor2(null);
                  }}
                  style={[
                    modernStyles.modernButton,
                    getHeroButtonStyle(theme, 'solid'),
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      backgroundColor: "transparent",
                      marginBottom: 8,
                      shadowOpacity: 0,
                    }
                  ]}
                >
                    <FontAwesome name="upload" color={theme ? "black" : "white"} size={16} />
                    <Title style={{ fontSize: 14, textAlign: "left", fontWeight: '600', color: theme ? "black" : "white" }}>Upgrade / Billing</Title>
                </Pressable>
                
                <Pressable
                  onPress={() => {
                    (navigation as any).navigate('Settings'); 
                    setBurgerMenuAnchor2(null);
                  }}
                  style={[
                    modernStyles.modernButton,
                    getHeroButtonStyle(theme, 'outline'),
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      backgroundColor: "transparent",
                      shadowOpacity: 0,
                      borderColor:"transparent",
                      marginBottom: 8,
                    }
                  ]}
                >
                    <FontAwesome name="gear" color={theme ? "black" : "white"} size={16} />
                  <Title style={{ fontSize: 14, fontWeight: '600', color: getModernTextColor(theme) }}>Settings</Title>
                </Pressable>
                
              </View>
                <Pressable
                  onPress={() => {
                    _handleLogout();
                    setBurgerMenuAnchor2(null);
                  }}
                  style={[
                    modernStyles.modernButton,
                    getModernActionStyle(theme, 'red'),
                    {
                      flexDirection: 'row',
                      alignSelf: "stretch",
                      alignItems: "center",
                      width:"100%",
                      backgroundColor: "transparent",
                      gap: 12,
                    }
                  ]}
                >
                  <MaterialIcons name="logout" color={"red"} size={16} />
                    <Title style={{ fontSize: 14, fontWeight: '600', color: theme ? "black" : "white" }}>{t("Logout")}</Title>
                </Pressable>
            </View>
            </MenuList>
          </Menu>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  statusBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    flex: 2,
    overflow: 'visible',
    zIndex: 2,
  },
  innerContainer: {
    flex: 7,
    marginHorizontal: 20,
    paddingLeft: 50,
    paddingRight: "2%"
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'row',
    alignSelf: 'flex-end',
    alignItems: 'center',
    alignContent: 'space-between',
    gap: 8, // Reduced gap to fit more buttons
    justifyContent: 'flex-end',
    minWidth: 350, // Increased minimum width for more buttons
  },
  iconWrapper: {
    maxWidth: 50,
  },
  iconContainer: {
    borderRadius: BORDERS,
    elevation: 1,
    backgroundColor: 'white',
    flex: 1,
    margin: 5,
  },
  lottieIcon: {
    flex: 1,
    height: 40,
    minWidth: 35
  },
  feedbackIcon: {
    flex: 1,
    height: 28,
    minHeight: 20,
    margin: 5,
    minWidth: 25
  },
  themeIcon: {
    flex: 1,
    alignSelf: 'center',
    elevation: 1,
    borderRadius: 5,
    margin: 5,
  },
  userProfileContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignContent: 'flex-start',
    alignItems: 'flex-start',
    flex: 1,
    flexWrap: 'nowrap',
  },
  userNameContainer: {
    flex: 1,
    flexDirection: 'row',
    gap: 5,
  },
  userText: {
    flex: 1,
    fontSize: 14,
    textAlign: 'left',
  },
});

// Memoize StatusBar component to prevent unnecessary re-renders
// The component will re-render when:
// - Props change (availableWidth, searchVal, searchExpanded, etc.)
// - User data changes (userId, user.id, authenticated) - handled by useMemo inside component
// - Redux state changes (notificationCount, theme, etc.) - these are necessary re-renders
const MemoizedStatusBar = memo(StatusBar, (prevProps, nextProps) => {
  // Compare props to prevent unnecessary re-renders when props haven't changed
  // User-related re-renders are handled internally via useMemo dependencies
  return (
    prevProps.availableWidth === nextProps.availableWidth &&
    prevProps.isSmallScreen === nextProps.isSmallScreen &&
    prevProps.searchVal === nextProps.searchVal &&
    prevProps.searchExpanded === nextProps.searchExpanded &&
    prevProps.onSearchSubmit === nextProps.onSearchSubmit &&
    prevProps.setSearchVal === nextProps.setSearchVal &&
    prevProps.setSearchExpanded === nextProps.setSearchExpanded
  );
});

export default MemoizedStatusBar;
